import React, { useState } from "react";
import { safeJSONParse } from "../../../utils/helper";
import styles from "../agent-response.module.scss";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import clsx from "clsx";

type EvaluationResultsAgentProps = {
  parsedContent: any;
};

type SubDimension = {
  score: number;
  rating: string;
  evidence?: string[];
  gaps?: string[];
};

type DimensionScore = {
  score: number;
  rating: string;
  category: string;
  evidence?: string[];
  gaps?: string[];
  sub_dimensions?: Record<string, SubDimension>;
};

type Summary = {
  overview: string;
  best_suited_for: string;
};

type EvaluatedAgent = {
  agent_name: string;
  overall_score: number;
  rating: string;
  dimension_scores?: Record<string, DimensionScore>;
  strengths?: string[];
  weaknesses?: string[];
  summary?: Summary;
};

type RejectedAgent = {
  file_name: string;
  status: string;
  reason: string;
  confidence: number;
};

export default function EvaluationResultsAgent({
  parsedContent,
}: EvaluationResultsAgentProps): JSX.Element {
  const answer =
    typeof parsedContent?.answer === "string"
      ? safeJSONParse(parsedContent.answer)
      : parsedContent?.answer || {};

  const evaluatedAgents: EvaluatedAgent[] = answer.evaluated_agents || [];
  const rejectedAgents: RejectedAgent[] = answer.rejected_agents || [];
  const question = parsedContent?.question || parsedContent?.prompt;

  // Debug logging
  console.log("EvaluationResultsAgent - Parsed Content:", parsedContent);
  console.log("EvaluationResultsAgent - Answer:", answer);
  console.log("EvaluationResultsAgent - Evaluated Agents:", evaluatedAgents);
  console.log("EvaluationResultsAgent - Rejected Agents:", rejectedAgents);

  // State for expand/collapse - one per dimension
  const [expandedDimensions, setExpandedDimensions] = useState<
    Record<string, boolean>
  >({});

  // State for expand/collapse - one per sub-dimension (shared across all agents)
  const [expandedSubDimensions, setExpandedSubDimensions] = useState<
    Record<string, boolean>
  >({});

  // State for strengths and weaknesses
  const [strengthsOpen, setStrengthsOpen] = useState(false);
  const [weaknessesOpen, setWeaknessesOpen] = useState(false);

  // State for summary
  const [summaryOpen, setSummaryOpen] = useState(true);

  const toggleDimension = (dimensionName: string) => {
    setExpandedDimensions((prev) => ({
      ...prev,
      [dimensionName]: !prev[dimensionName],
    }));
  };

  const toggleSubDimension = (dimensionName: string, subDimName: string) => {
    const key = `${dimensionName}-${subDimName}`;
    setExpandedSubDimensions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const isSubDimensionExpanded = (
    dimensionName: string,
    subDimName: string,
  ): boolean => {
    const key = `${dimensionName}-${subDimName}`;
    return expandedSubDimensions[key] || false;
  };

  // Get all dimension names from the first agent (assuming all have same dimensions)
  const dimensionNames =
    evaluatedAgents.length > 0 && evaluatedAgents[0]?.dimension_scores
      ? Object.keys(evaluatedAgents[0].dimension_scores)
      : [];

  // Rating color and label mapping
  const getRatingStyle = (rating: string) => {
    switch (rating) {
      case "H":
        return {
          label: "High",
          className: styles.high,
          customStyle: {},
        };
      case "M":
        return {
          label: "Medium",
          className: styles.low,
          customStyle: {},
        };
      case "L":
        return {
          label: "Low",
          className: styles.medium,
          customStyle: {},
        };
      default:
        return {
          label: rating,
          className: "",
          customStyle: { backgroundColor: "#888", color: "white" },
        };
    }
  };

  if (evaluatedAgents.length === 0 && rejectedAgents.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
          No evaluation results available
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        color: "#333",
        width: "100%",
        overflowX: "auto",
      }}
    >
      {/* Evaluated Agents Table */}
      {evaluatedAgents.length > 0 && (
        <>
          <div style={{ marginBottom: "16px" }}>
            <h4
              style={{
                color: "#004141",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              Agent Evaluation Results
            </h4>
            {/* <p style={{ fontSize: "14px", color: "#536174", margin: "8px 0" }}>
              Comprehensive evaluation across core capabilities and
              organizational requirements
            </p> */}
          </div>

          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: "20px",
              marginBottom: "40px",
              tableLayout: "fixed",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: 8,
                    width: "30%",
                    minWidth: evaluatedAgents.length >= 3 ? "350px" : "auto",
                    color: "#004141",
                    fontSize: 12,
                  }}
                >
                  Dimension
                </th>
                {evaluatedAgents.map((agent, idx) => {
                  const agentColumnWidth =
                    evaluatedAgents.length > 0
                      ? `${70 / evaluatedAgents.length}%`
                      : "auto";

                  return (
                    <th
                      key={idx}
                      style={{
                        border: "1px solid #ddd",
                        padding: "8px",
                        color: "#004141",
                        fontSize: 12,
                        width: agentColumnWidth,
                      }}
                    >
                      <div style={{ fontWeight: 500 }}>{agent.agent_name}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {/* Summary Row */}
              {evaluatedAgents.some((agent) => agent.summary) && (
                <>
                  <tr
                    style={{
                      cursor: "pointer",
                      backgroundColor: "#f8f9fa",
                    }}
                    onClick={() => setSummaryOpen(!summaryOpen)}
                  >
                    <td
                      style={{
                        border: "1px solid #ddd",
                        borderRight: "none",
                        padding: "4px",
                        fontWeight: 500,
                        fontSize: 11,
                        minWidth:
                          evaluatedAgents.length >= 3 ? "350px" : "auto",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <span style={{ flexShrink: 0 }}>
                          {summaryOpen ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </span>
                        <span style={{ lineHeight: "1.3" }}>
                          Summary & Best Use Cases
                        </span>
                      </div>
                    </td>
                    {evaluatedAgents.map((agent, agentIdx) => {
                      const isLast = agentIdx === evaluatedAgents.length - 1;
                      const hasSummary = !!agent.summary;

                      return (
                        <td
                          key={agentIdx}
                          style={{
                            borderTop: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            borderLeft: "none",
                            borderRight: isLast ? "1px solid #ddd" : "none",
                            padding: "4px",
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "11px",
                              color: hasSummary ? "#333" : "#888",
                              fontWeight: hasSummary ? 500 : 400,
                            }}
                          >
                            {hasSummary ? "Available" : "-"}
                          </span>
                        </td>
                      );
                    })}
                  </tr>

                  {/* Summary Details */}
                  {summaryOpen && (
                    <>
                      {/* Overview Row */}
                      <tr>
                        <td
                          style={{
                            border: "1px solid #ddd",
                            padding: "4px",
                            paddingLeft: "20px",
                            fontSize: 11,
                            verticalAlign: "middle",
                          }}
                        >
                          Overview
                        </td>
                        {evaluatedAgents.map((agent, agentIdx) => {
                          const hasOverview = !!agent.summary?.overview;

                          return (
                            <td
                              key={agentIdx}
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px",
                                fontSize: 11,
                                textAlign: "left",
                                verticalAlign: "top",
                                lineHeight: "1.6",
                              }}
                            >
                              {hasOverview ? (
                                <span style={{ color: "#403f3f" }}>
                                  {agent.summary?.overview}
                                </span>
                              ) : (
                                <span
                                  style={{
                                    color: "#888",
                                    textAlign: "center",
                                    display: "block",
                                  }}
                                >
                                  -
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      {/* Best Suited For Row */}
                      <tr>
                        <td
                          style={{
                            border: "1px solid #ddd",
                            padding: "4px",
                            paddingLeft: "20px",
                            fontSize: 11,
                            verticalAlign: "middle",
                          }}
                        >
                          Best Suited For
                        </td>
                        {evaluatedAgents.map((agent, agentIdx) => {
                          const hasBestSuitedFor =
                            !!agent.summary?.best_suited_for;

                          return (
                            <td
                              key={agentIdx}
                              style={{
                                border: "1px solid #ddd",
                                padding: "8px",
                                fontSize: 11,
                                textAlign: "left",
                                verticalAlign: "top",
                                lineHeight: "1.6",
                              }}
                            >
                              {hasBestSuitedFor ? (
                                <span style={{ color: "#403f3f" }}>
                                  {agent.summary?.best_suited_for}
                                </span>
                              ) : (
                                <span
                                  style={{
                                    color: "#888",
                                    textAlign: "center",
                                    display: "block",
                                  }}
                                >
                                  -
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    </>
                  )}
                </>
              )}

              {dimensionNames.map((dimensionName, dimIdx) => (
                <React.Fragment key={dimIdx}>
                  {/* Main Dimension Row */}
                  <tr
                    style={{
                      cursor: "pointer",
                      backgroundColor: "#f8f9fa",
                    }}
                    onClick={() => toggleDimension(dimensionName)}
                  >
                    <td
                      style={{
                        border: "1px solid #ddd",
                        borderRight: "none",
                        padding: "4px",
                        fontWeight: 500,
                        fontSize: 11,
                        minWidth:
                          evaluatedAgents.length >= 3 ? "350px" : "auto",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <span style={{ flexShrink: 0 }}>
                          {expandedDimensions[dimensionName] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </span>
                        <span style={{ lineHeight: "1.3" }}>
                          {dimensionName}
                        </span>
                      </div>
                    </td>
                    {evaluatedAgents.map((agent, agentIdx) => {
                      const dimension = agent.dimension_scores?.[dimensionName];
                      const isLast = agentIdx === evaluatedAgents.length - 1;

                      if (!dimension) {
                        return (
                          <td
                            key={agentIdx}
                            style={{
                              borderTop: "1px solid #ddd",
                              borderBottom: "1px solid #ddd",
                              borderLeft: "none",
                              borderRight: isLast ? "1px solid #ddd" : "none",
                              padding: "8px",
                              textAlign: "center",
                              verticalAlign: "middle",
                            }}
                          >
                            <span style={{ fontSize: "11px", color: "#888" }}>
                              N/A
                            </span>
                          </td>
                        );
                      }

                      const ratingStyle = getRatingStyle(dimension.rating);

                      return (
                        <td
                          key={agentIdx}
                          style={{
                            borderTop: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            borderLeft: "none",
                            borderRight: isLast ? "1px solid #ddd" : "none",
                            padding: "4px",
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          <span
                            className={clsx(
                              styles.score,
                              ratingStyle.className,
                            )}
                            style={ratingStyle.customStyle}
                          >
                            {ratingStyle.label}
                          </span>
                        </td>
                      );
                    })}
                  </tr>

                  {/* Sub-dimensions Rows */}
                  {expandedDimensions[dimensionName] &&
                    evaluatedAgents[0]?.dimension_scores?.[dimensionName]
                      ?.sub_dimensions &&
                    Object.entries(
                      evaluatedAgents[0].dimension_scores[dimensionName]
                        .sub_dimensions,
                    ).map(([subDimName, _], subIdx) => {
                      // Check if any agent has details for this sub-dimension
                      const hasAnyDetails = evaluatedAgents.some(
                        (agent) =>
                          (agent.dimension_scores?.[dimensionName]
                            ?.sub_dimensions?.[subDimName]?.evidence?.length ||
                            0) > 0 ||
                          (agent.dimension_scores?.[dimensionName]
                            ?.sub_dimensions?.[subDimName]?.gaps?.length || 0) >
                            0,
                      );
                      const isExpanded = isSubDimensionExpanded(
                        dimensionName,
                        subDimName,
                      );

                      return (
                        <React.Fragment key={subIdx}>
                          <tr
                            style={{
                              cursor: hasAnyDetails ? "pointer" : "default",
                            }}
                            onClick={() =>
                              hasAnyDetails &&
                              toggleSubDimension(dimensionName, subDimName)
                            }
                          >
                            <td
                              style={{
                                border: "1px solid #ddd",
                                padding: "4px",
                                paddingLeft: "20px",
                                fontSize: 11,
                                verticalAlign: "middle",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                {hasAnyDetails && (
                                  <span style={{ flexShrink: 0 }}>
                                    {isExpanded ? (
                                      <ExpandLessIcon fontSize="small" />
                                    ) : (
                                      <ExpandMoreIcon fontSize="small" />
                                    )}
                                  </span>
                                )}
                                <span>{subDimName}</span>
                              </div>
                            </td>

                            {evaluatedAgents.map((agent, agentIdx) => {
                              const subDimension =
                                agent.dimension_scores?.[dimensionName]
                                  ?.sub_dimensions?.[subDimName];

                              if (!subDimension) {
                                return (
                                  <td
                                    key={agentIdx}
                                    style={{
                                      border: "1px solid #ddd",
                                      padding: "4px",
                                      textAlign: "center",
                                      verticalAlign: "middle",
                                      fontSize: 11,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "11px",
                                        color: "#888",
                                      }}
                                    >
                                      N/A
                                    </span>
                                  </td>
                                );
                              }

                              const ratingStyle = getRatingStyle(
                                subDimension.rating,
                              );

                              return (
                                <td
                                  key={agentIdx}
                                  style={{
                                    border: "1px solid #ddd",
                                    padding: "4px",
                                    textAlign: "center",
                                    verticalAlign: "middle",
                                    fontSize: 11,
                                  }}
                                >
                                  <span
                                    className={clsx(
                                      styles.score,
                                      ratingStyle.className,
                                    )}
                                    style={{
                                      fontSize: "10px",
                                      ...ratingStyle.customStyle,
                                    }}
                                  >
                                    {ratingStyle.label}
                                  </span>
                                </td>
                              );
                            })}
                          </tr>

                          {/* Evidence Row (Collapsible) */}
                          {isExpanded && hasAnyDetails && (
                            <>
                              {evaluatedAgents.some(
                                (agent) =>
                                  (agent.dimension_scores?.[dimensionName]
                                    ?.sub_dimensions?.[subDimName]?.evidence
                                    ?.length || 0) > 0,
                              ) && (
                                <tr>
                                  <td
                                    style={{
                                      border: "1px solid #ddd",
                                      padding: "4px",
                                      paddingLeft: "50px",
                                      fontSize: 11,
                                      fontWeight: 600,
                                      verticalAlign: "middle",
                                      backgroundColor: "#f8f9fa",
                                    }}
                                  >
                                    <span style={{ color: "#267103" }}>
                                      ✓ Evidence
                                    </span>
                                  </td>
                                  {evaluatedAgents.map((agent, agentIdx) => {
                                    const subDimension =
                                      agent.dimension_scores?.[dimensionName]
                                        ?.sub_dimensions?.[subDimName];
                                    const hasEvidence =
                                      subDimension &&
                                      (subDimension.evidence?.length || 0) > 0;

                                    return (
                                      <td
                                        key={agentIdx}
                                        style={{
                                          border: "1px solid #ddd",
                                          padding: "8px",
                                          fontSize: 10,
                                          textAlign: "center",
                                          verticalAlign: "top",
                                          backgroundColor: "#f8f9fa",
                                        }}
                                      >
                                        {hasEvidence ? (
                                          <div
                                            style={{
                                              display: "inline-block",
                                              textAlign: "left",
                                            }}
                                          >
                                            <ul
                                              style={{
                                                margin: 0,
                                                paddingLeft: "16px",
                                                color: "#403f3f",
                                              }}
                                            >
                                              {subDimension.evidence?.map(
                                                (e, eIdx) => (
                                                  <li key={eIdx}>{e}</li>
                                                ),
                                              )}
                                            </ul>
                                          </div>
                                        ) : (
                                          <span style={{ color: "#888" }}>
                                            -
                                          </span>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              )}

                              {/* Gaps Row (Collapsible) */}
                              {evaluatedAgents.some(
                                (agent) =>
                                  (agent.dimension_scores?.[dimensionName]
                                    ?.sub_dimensions?.[subDimName]?.gaps
                                    ?.length || 0) > 0,
                              ) && (
                                <tr>
                                  <td
                                    style={{
                                      border: "1px solid #ddd",
                                      padding: "4px",
                                      paddingLeft: "50px",
                                      fontSize: 11,
                                      fontWeight: 600,
                                      verticalAlign: "middle",
                                      backgroundColor: "#f8f9fa",
                                    }}
                                  >
                                    <span style={{ color: "#bd1919" }}>
                                      ✗ Gaps
                                    </span>
                                  </td>
                                  {evaluatedAgents.map((agent, agentIdx) => {
                                    const subDimension =
                                      agent.dimension_scores?.[dimensionName]
                                        ?.sub_dimensions?.[subDimName];
                                    const hasGaps =
                                      subDimension &&
                                      (subDimension.gaps?.length || 0) > 0;

                                    return (
                                      <td
                                        key={agentIdx}
                                        style={{
                                          border: "1px solid #ddd",
                                          padding: "8px",
                                          fontSize: 10,
                                          textAlign: "center",
                                          verticalAlign: "top",
                                          backgroundColor: "#f8f9fa",
                                        }}
                                      >
                                        {hasGaps ? (
                                          <div
                                            style={{
                                              display: "inline-block",
                                              textAlign: "left",
                                            }}
                                          >
                                            <ul
                                              style={{
                                                margin: 0,
                                                paddingLeft: "16px",
                                                color: "#403f3f",
                                              }}
                                            >
                                              {subDimension.gaps?.map(
                                                (g, gIdx) => (
                                                  <li key={gIdx}>{g}</li>
                                                ),
                                              )}
                                            </ul>
                                          </div>
                                        ) : (
                                          <span style={{ color: "#888" }}>
                                            -
                                          </span>
                                        )}
                                      </td>
                                    );
                                  })}
                                </tr>
                              )}
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}

                  {/* Dimension Evidence and Gaps Row */}
                  {expandedDimensions[dimensionName] && (
                    <tr>
                      <td
                        style={{
                          border: "1px solid #ddd",
                          padding: "4px",
                          paddingLeft: "20px",
                          fontSize: 11,
                          verticalAlign: "middle",
                        }}
                      >
                        Overall Assessment
                      </td>
                      {evaluatedAgents.map((agent, agentIdx) => {
                        const dimension =
                          agent.dimension_scores?.[dimensionName];
                        return (
                          <td
                            key={agentIdx}
                            style={{
                              border: "1px solid #ddd",
                              padding: "4px",
                              fontSize: 11,
                              textAlign: "center",
                              verticalAlign: "top",
                            }}
                          >
                            <div
                              style={{
                                display: "inline-block",
                                textAlign: "center",
                              }}
                            >
                              {dimension &&
                                (dimension.evidence?.length || 0) > 0 && (
                                  <div style={{ marginBottom: "8px" }}>
                                    <div
                                      style={{
                                        fontWeight: 600,
                                        color: "#267103",
                                        marginBottom: "4px",
                                        textAlign: "center",
                                      }}
                                    >
                                      ✓ Evidence:
                                    </div>
                                    <ul
                                      style={{
                                        margin: 0,
                                        paddingLeft: "16px",
                                        color: "#403f3f",
                                        textAlign: "left",
                                      }}
                                    >
                                      {dimension.evidence?.map((e, eIdx) => (
                                        <li key={eIdx}>{e}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              {dimension &&
                                (dimension.gaps?.length || 0) > 0 && (
                                  <div>
                                    <div
                                      style={{
                                        fontWeight: 600,
                                        color: "#bd1919",
                                        marginBottom: "4px",
                                        textAlign: "center",
                                      }}
                                    >
                                      ✗ Gaps:
                                    </div>
                                    <ul
                                      style={{
                                        margin: 0,
                                        paddingLeft: "16px",
                                        color: "#403f3f",
                                        textAlign: "left",
                                      }}
                                    >
                                      {dimension.gaps?.map((g, gIdx) => (
                                        <li key={gIdx}>{g}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  )}
                </React.Fragment>
              ))}

              {/* Strengths Row */}
              {evaluatedAgents.some(
                (agent) => (agent.strengths?.length || 0) > 0,
              ) && (
                <>
                  <tr
                    style={{
                      cursor: "pointer",
                      backgroundColor: "#f8f9fa",
                    }}
                    onClick={() => setStrengthsOpen(!strengthsOpen)}
                  >
                    <td
                      style={{
                        border: "1px solid #ddd",
                        borderRight: "none",
                        padding: "4px",
                        fontWeight: 500,
                        fontSize: 11,
                        minWidth:
                          evaluatedAgents.length >= 3 ? "350px" : "auto",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <span style={{ flexShrink: 0 }}>
                          {strengthsOpen ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </span>
                        <span style={{ lineHeight: "1.3" }}>
                          On Paper Evidence
                        </span>
                      </div>
                    </td>
                    {evaluatedAgents.map((agent, agentIdx) => {
                      const isLast = agentIdx === evaluatedAgents.length - 1;
                      const hasStrengths = (agent.strengths?.length || 0) > 0;

                      return (
                        <td
                          key={agentIdx}
                          style={{
                            borderTop: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            borderLeft: "none",
                            borderRight: isLast ? "1px solid #ddd" : "none",
                            padding: "4px",
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "11px",
                              color: hasStrengths ? "#267103" : "#888",
                              fontWeight: hasStrengths ? 500 : 400,
                            }}
                          >
                            {hasStrengths
                              ? `${agent.strengths?.length} ${agent.strengths?.length === 1 ? "item" : "items"}`
                              : "-"}
                          </span>
                        </td>
                      );
                    })}
                  </tr>

                  {/* Strengths Details */}
                  {strengthsOpen && (
                    <tr>
                      <td
                        style={{
                          border: "1px solid #ddd",
                          padding: "4px",
                          paddingLeft: "20px",
                          fontSize: 11,
                          verticalAlign: "middle",
                        }}
                      >
                        Details
                      </td>
                      {evaluatedAgents.map((agent, agentIdx) => {
                        const hasStrengths = (agent.strengths?.length || 0) > 0;

                        return (
                          <td
                            key={agentIdx}
                            style={{
                              border: "1px solid #ddd",
                              padding: "4px",
                              fontSize: 11,
                              textAlign: hasStrengths ? "left" : "center",
                              verticalAlign: "top",
                            }}
                          >
                            {hasStrengths ? (
                              <ul
                                style={{
                                  margin: 0,
                                  paddingLeft: "16px",
                                  color: "#403f3f",
                                }}
                              >
                                {agent.strengths?.map((s, sIdx) => (
                                  <li key={sIdx}>{s}</li>
                                ))}
                              </ul>
                            ) : (
                              <span style={{ color: "#888" }}>-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                </>
              )}

              {/* Weaknesses Row */}
              {evaluatedAgents.some(
                (agent) => (agent.weaknesses?.length || 0) > 0,
              ) && (
                <>
                  <tr
                    style={{
                      cursor: "pointer",
                      backgroundColor: "#f8f9fa",
                    }}
                    onClick={() => setWeaknessesOpen(!weaknessesOpen)}
                  >
                    <td
                      style={{
                        border: "1px solid #ddd",
                        borderRight: "none",
                        padding: "4px",
                        fontWeight: 500,
                        fontSize: 11,
                        minWidth:
                          evaluatedAgents.length >= 3 ? "350px" : "auto",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <span style={{ flexShrink: 0 }}>
                          {weaknessesOpen ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </span>
                        <span style={{ lineHeight: "1.3" }}>
                          Things to be checked
                        </span>
                      </div>
                    </td>
                    {evaluatedAgents.map((agent, agentIdx) => {
                      const isLast = agentIdx === evaluatedAgents.length - 1;
                      const hasWeaknesses = (agent.weaknesses?.length || 0) > 0;

                      return (
                        <td
                          key={agentIdx}
                          style={{
                            borderTop: "1px solid #ddd",
                            borderBottom: "1px solid #ddd",
                            borderLeft: "none",
                            borderRight: isLast ? "1px solid #ddd" : "none",
                            padding: "4px",
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "11px",
                              color: hasWeaknesses ? "#bd1919" : "#888",
                              fontWeight: hasWeaknesses ? 500 : 400,
                            }}
                          >
                            {hasWeaknesses
                              ? `${agent.weaknesses?.length} ${agent.weaknesses?.length === 1 ? "item" : "items"}`
                              : "-"}
                          </span>
                        </td>
                      );
                    })}
                  </tr>

                  {/* Weaknesses Details */}
                  {weaknessesOpen && (
                    <tr>
                      <td
                        style={{
                          border: "1px solid #ddd",
                          padding: "4px",
                          paddingLeft: "20px",
                          fontSize: 11,
                          verticalAlign: "middle",
                        }}
                      >
                        Details
                      </td>
                      {evaluatedAgents.map((agent, agentIdx) => {
                        const hasWeaknesses =
                          (agent.weaknesses?.length || 0) > 0;

                        return (
                          <td
                            key={agentIdx}
                            style={{
                              border: "1px solid #ddd",
                              padding: "4px",
                              fontSize: 11,
                              textAlign: hasWeaknesses ? "left" : "center",
                              verticalAlign: "top",
                            }}
                          >
                            {hasWeaknesses ? (
                              <ul
                                style={{
                                  margin: 0,
                                  paddingLeft: "16px",
                                  color: "#403f3f",
                                }}
                              >
                                {agent.weaknesses?.map((w, wIdx) => (
                                  <li key={wIdx}>{w}</li>
                                ))}
                              </ul>
                            ) : (
                              <span style={{ color: "#888" }}>-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </>
      )}

      {/* Rejected Agents Section */}
      {rejectedAgents.length > 0 && (
        <div style={{ marginBottom: "30px" }}>
          <h4
            style={{
              color: "#004141",
              fontSize: "16px",
              fontWeight: 600,
              marginBottom: "12px",
            }}
          >
            Rejected Profiles
          </h4>
          {rejectedAgents.map((rejected, idx) => {
            const isHumanResume =
              rejected.reason
                ?.toLowerCase()
                .includes("human_resume_detected") ||
              rejected.reason?.toLowerCase().includes("human resume") ||
              rejected.reason?.toLowerCase().includes("human cv");

            const message = isHumanResume
              ? "This profile has been rejected as it appears to be a human CV rather than an agent profile"
              : rejected.reason;

            return (
              <div
                key={idx}
                style={{
                  backgroundColor: "#fadada",
                  border: "1px solid #bd1919",
                  borderRadius: "8px",
                  padding: "12px 16px",
                  marginBottom: "8px",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#bd1919",
                    marginBottom: "4px",
                  }}
                >
                  {rejected.file_name}
                </div>
                <div style={{ fontSize: "13px", color: "#403f3f" }}>
                  {message}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Final Question */}
      {question && <div className={styles.finalQuestion}>{question}</div>}
    </div>
  );
}
